const CACHE_RETRY_PARAM = 'rp-cache-retry';

// One value per JavaScript page lifetime. Every failed cover gets its own
// retry, while duplicate cover URLs share the same cache-busting value so the
// browser can reuse the recovered response instead of downloading it twice.
const PAGE_RETRY_TOKEN = (() => {
  try {
    return globalThis.crypto?.randomUUID?.()
      || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
})();

// Track the canonical cover URL attempted by each image. A component may
// reuse an <img> for a different cover later; that new URL still gets one
// recovery attempt, while the cache-busted retry of the same URL does not.
const retriedCoverUrls = new WeakMap();
const recoveryLayers = new WeakMap();

function coverUrlFor(img) {
  const src = img?.getAttribute?.('src');
  if (!src) return null;

  try {
    const base = img.ownerDocument?.baseURI || globalThis.location?.href || 'http://localhost/';
    const url = new URL(src, base);
    const page = new URL(base);
    if (url.origin !== page.origin) return null;
    if (!/^\/api\/library\/artwork\/cover\/[^/]+$/.test(url.pathname)) return null;
    return url;
  } catch {
    return null;
  }
}

/** Return true when `img` currently points at the first-party cover route. */
export function isArtworkCoverImage(img) {
  return coverUrlFor(img) !== null;
}

/**
 * Retry the image's current artwork-cover URL once with a page-scoped cache
 * buster. Returns true only when it scheduled that retry.
 */
export function retryArtworkCover(img) {
  const url = coverUrlFor(img);
  if (!url) return false;

  const canonical = new URL(url);
  canonical.searchParams.delete(CACHE_RETRY_PARAM);
  const canonicalKey = canonical.href;
  if (retriedCoverUrls.get(img) === canonicalKey) return false;

  retriedCoverUrls.set(img, canonicalKey);
  url.searchParams.set(CACHE_RETRY_PARAM, PAGE_RETRY_TOKEN);
  img.setAttribute('src', `${url.pathname}${url.search}${url.hash}`);
  return true;
}

/**
 * Install page-level cover recovery before target and inline error handlers.
 * The first failure is intercepted only when a cache-busted retry is started;
 * a failed retry continues through the caller's existing fallback unchanged.
 */
export function installArtworkCoverRecoveryLayer(root = globalThis.document) {
  if (!root?.addEventListener) return () => {};

  const existing = recoveryLayers.get(root);
  if (existing) return existing.remove;

  const onError = (event) => {
    if (!retryArtworkCover(event.target)) return;
    event.stopPropagation();
  };

  const remove = () => {
    if (recoveryLayers.get(root)?.onError !== onError) return;
    root.removeEventListener('error', onError, true);
    recoveryLayers.delete(root);
  };

  recoveryLayers.set(root, { onError, remove });
  root.addEventListener('error', onError, true);
  return remove;
}

/**
 * Install the common two-stage cover error flow: one cache-busted retry, then
 * the caller's existing placeholder/fallback behavior.
 */
export function installArtworkCoverRecovery(img, onFinalFailure) {
  const onError = () => {
    if (retryArtworkCover(img)) return;
    img.removeEventListener('error', onError);
    onFinalFailure(img);
  };
  img.addEventListener('error', onError);
  return () => img.removeEventListener('error', onError);
}
